import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const UPDATED = "June 2026";

const SECTIONS = [
  {
    h: "1. Who we are",
    b: [
      "ESP26 is a clinical workforce management platform operated by ESP26 (\u201cwe\u201d, \u201cus\u201d). We act as a data controller for the personal data held about our workforce within the platform.",
      "Questions about this policy or your data can be sent to our Data Protection Officer at dpo@esp26.uk.",
    ],
  },
  {
    h: "2. What data we collect",
    b: [
      "Identity & contact data: name, work and personal phone numbers, work email, home address and postcode.",
      "Employment data: job role, region, assigned manager (RCSM) and clinical assessors (CNAT), employment status and start date.",
      "Training & performance data: eLearning completion, competency and skills levels, supervisions, reviews, assessments and field-visit records.",
      "Documents: certificates, identity and compliance documents uploaded against a profile.",
      "Usage data: essential session cookies and, where you consent, analytics cookies.",
    ],
  },
  {
    h: "3. How we use it and our lawful basis",
    b: [
      "We process this data to manage the clinical workforce, meet employment and clinical governance obligations, and keep training and compliance up to date.",
      "Our lawful bases are the performance of the employment contract, compliance with legal obligations, and our legitimate interest in running a safe, compliant clinical operation. Analytics cookies are used only with your consent.",
    ],
  },
  {
    h: "4. Cookies",
    b: [
      "Essential cookies keep you signed in and secure the platform \u2014 these cannot be switched off.",
      "Optional analytics cookies help us understand and improve the product. We only set these after you choose \u201cAccept all\u201d in the cookie banner. Choosing \u201cReject non-essential\u201d opts you out of analytics.",
    ],
  },
  {
    h: "5. How long we keep it",
    b: [
      "We retain personal data for as long as an individual is part of the workforce and for the period required by our legal, clinical and employment obligations, after which it is anonymised or deleted.",
    ],
  },
  {
    h: "6. Your rights",
    b: [
      "You have the right to access the personal data we hold about you and to receive a copy (data portability). Authorised administrators can produce a complete data export from your profile.",
      "You have the right to rectification, to restrict or object to processing, to withdraw consent, and the right to erasure. When erasure is applied, your identifying data is anonymised and associated documents are removed, with the action recorded in an audit log.",
      "To exercise any of these rights, contact dpo@esp26.uk.",
    ],
  },
  {
    h: "7. Data security & sharing",
    b: [
      "Access is role-based: users only see the records they are responsible for. Data is protected with authenticated, encrypted access and is not sold to third parties.",
      "We share data with processors (e.g. training providers such as eLeaP) only as needed to deliver the service and under appropriate agreements.",
    ],
  },
  {
    h: "8. Changes to this policy",
    b: [
      "We may update this policy from time to time. The date below reflects the latest revision.",
    ],
  },
];

export default function Privacy() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#0F172A] text-white" data-testid="privacy-page">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0F172A]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4 lg:px-8">
          <Link to="/" className="flex items-center gap-3" data-testid="privacy-brand">
            <img src="/logo.png" alt="ESP26" className="h-9 w-9 rounded-lg object-cover" />
            <span className="font-heading text-lg font-extrabold tracking-tight">ESP26</span>
          </Link>
          <button
            onClick={() => navigate(user ? "/dashboard" : "/")}
            data-testid="privacy-back-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-slate-200 transition-colors hover:bg-white/10"
          >
            <ArrowLeft className="h-4 w-4" /> {user ? "Back to app" : "Back to home"}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-16 lg:px-8">
        <span className="inline-flex items-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-orange-400">
          <ShieldCheck className="h-3.5 w-3.5" /> GDPR
        </span>
        <h1 className="mt-5 font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">
          Privacy &amp; Cookie Policy
        </h1>
        <p className="mt-4 max-w-2xl text-slate-400">
          How ESP26 collects, uses, stores and protects personal data, and the rights available to the people whose data
          we hold.
        </p>
        <p className="mt-2 text-sm text-slate-500">Last updated: {UPDATED}</p>

        <div className="mt-12 space-y-10">
          {SECTIONS.map((s) => (
            <section key={s.h}>
              <h2 className="font-heading text-xl font-bold text-white">{s.h}</h2>
              <div className="mt-3 space-y-3">
                {s.b.map((p, idx) => (
                  <p key={idx} className="text-[15px] leading-relaxed text-slate-300">
                    {p}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-14 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
          <p className="font-heading text-lg font-bold">Contact our Data Protection Officer</p>
          <p className="mt-2 text-sm text-slate-400">
            Email{" "}
            <a href="mailto:dpo@esp26.uk" className="font-semibold text-orange-400 hover:underline">
              dpo@esp26.uk
            </a>{" "}
            for any data protection request or question.
          </p>
        </div>
      </main>

      <footer className="border-t border-white/10 py-8">
        <div className="mx-auto max-w-4xl px-5 text-sm text-slate-500 lg:px-8">
          © {new Date().getFullYear()} ESP26 — Clinical Workforce Management Platform.
        </div>
      </footer>
    </div>
  );
}
